import type { Column } from "@judo/model-api";
import type { GridFilterModel, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { convertFilterModelToQueryCustomizer } from "./table-filter-utils";
import { convertSortModelToOrderBy } from "./table-sort-utils";

/**
 * QueryCustomizer structure for REST API requests.
 * Includes sorting, pagination, filter criteria, and optional mask.
 */
export interface QueryCustomizer {
	_mask?: string;
	_orderBy?: { attribute: string; descending: boolean }[];
	_seek?: { limit: number; offset: number };
	[filterField: string]: unknown;
}

/**
 * Parameters for building a QueryCustomizer from DataGrid state.
 */
export interface BuildQueryCustomizerParams {
	sortModel: GridSortModel;
	paginationModel: GridPaginationModel;
	filterModel: GridFilterModel;
	columns: Column[];
	/** Pre-computed mask string to include in the query customizer */
	mask?: string;
}

/**
 * Build a QueryCustomizer object from DataGrid state (sort, pagination, filter).
 * This is used for server-side table operations to include current state in API requests.
 *
 * @param params - The DataGrid state models and column definitions
 * @returns QueryCustomizer object ready to be spread into an action
 */
export function buildQueryCustomizer(params: BuildQueryCustomizerParams): QueryCustomizer {
	const { sortModel, paginationModel, filterModel, columns, mask } = params;

	const orderBy = convertSortModelToOrderBy(sortModel);
	const filters = convertFilterModelToQueryCustomizer(filterModel, columns);

	return {
		...(mask ? { _mask: mask } : {}),
		_orderBy: orderBy.length > 0 ? orderBy : undefined,
		_seek: {
			limit: paginationModel.pageSize,
			offset: paginationModel.page * paginationModel.pageSize,
		},
		// Spread filters at the same level as _orderBy and _seek
		...filters,
	};
}
